import React, { useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { useActivation } from '../../../utils/activationUtil';
import { BulkProtectionOptions, StatusBadgeOptions } from '../../../utils/enums';
import { useStores } from '../../store';
import InfoButton from '@svg/info.svg?react';
import { Popover } from 'antd';
import './status-badge.scss';

interface StatusBadgeProps {
    isRecommended?: boolean | null;
    pageName: BulkProtectionOptions;
}

export const StatusBadge: React.FC<StatusBadgeProps> = observer(({isRecommended, pageName}) => {

    const { fwVersionStore, ruleStore } = useStores();

    const {
        AllowUntrustDest, AllowUntrustSrc,
        BlockUntrustDest, BlockUntrustSrc
    } = ruleStore;

    const {
        isAllPageRulesDisabled, isPartialInactive,
        getPageDisplayName, isOutboundActive, isInboundActive
    } = useActivation();

    const status: string = useMemo(() => {
        if (isAllPageRulesDisabled(pageName)) {
            return StatusBadgeOptions.INACTIVE;
        }

        if (isPartialInactive(pageName)) {
            return StatusBadgeOptions.PARTIALLY_INACTIVE;
        }

        if (isInboundActive(pageName)) {
            return StatusBadgeOptions.INBOUND_INACTIVE;
        }

        if (isOutboundActive(pageName)) {
            return StatusBadgeOptions.OUTBOUND_INACTIVE;
        }

        if (isRecommended) {
            return StatusBadgeOptions.ATT_RECOMMENDED;
        }

        if (isRecommended === false) {
            return StatusBadgeOptions.CUSTOM;
        }

        return '';
    }, [pageName, fwVersionStore.selectedFwVersion?.content.policy]);

    const allowIpPartialInactiveTooltip = () => {
        if (AllowUntrustDest?.disabled) {
            return 'Allowed IPs outbound direction is inactive. Click to Activate in the Firewall Rules tab.';
        }
        if (AllowUntrustSrc?.disabled) {
            return 'Allowed IPs inbound direction is inactive. Click to Activate in the Firewall Rules tab.';
        }
        return '';
    };

    const blockIpPartialInactiveTooltip = () => {
        const tooltipMessage: string[] = [];
        if (BlockUntrustDest?.disabled) {
            tooltipMessage.push('Blocked IPs outbound destination direction is inactive. Click to Activate in the Firewall Rules tab.');
        }
        if (BlockUntrustSrc?.disabled) {
            tooltipMessage.push('Blocked IPs inbound source direction is inactive. Click to Activate in the Firewall Rules tab.');
        }
        return tooltipMessage.join('\n');
    };

    type TooltipMap = {
        [key in BulkProtectionOptions.ALLOW_LIST | BulkProtectionOptions.BLOCK_LIST]: {
            [key in StatusBadgeOptions.INACTIVE | StatusBadgeOptions.PARTIALLY_INACTIVE]?: () => string;
        }
    };

    const ipControlTooltipCalculation: TooltipMap = {
        [BulkProtectionOptions.BLOCK_LIST]:
            {
                [StatusBadgeOptions.INACTIVE]: () => 'Blocked IPs are inactive. Click to Activate in the Firewall Rules tab.',
                [StatusBadgeOptions.PARTIALLY_INACTIVE]: blockIpPartialInactiveTooltip
            },
        [BulkProtectionOptions.ALLOW_LIST]:
            {
                [StatusBadgeOptions.INACTIVE]: () => 'Allowed IPs are inactive. Click to Activate in the Firewall Rules tab.',
                [StatusBadgeOptions.PARTIALLY_INACTIVE]: allowIpPartialInactiveTooltip
            }
    };

    const getTooltipMessage = () => {
        if(pageName === BulkProtectionOptions.BLOCK_LIST || pageName === BulkProtectionOptions.ALLOW_LIST) {
            return ipControlTooltipCalculation[pageName][status as StatusBadgeOptions.INACTIVE | StatusBadgeOptions.PARTIALLY_INACTIVE]?.() || '';
        }
        if (status === StatusBadgeOptions.PARTIALLY_INACTIVE) {
            return `${getPageDisplayName(pageName)} configurations is partially Inactive. Click to Activate from the menu.`;
        }
        if (status === StatusBadgeOptions.INBOUND_INACTIVE) {
            return `Inbound ${getPageDisplayName(pageName)} configuration is Inactive. Click to Activate from the menu.`;
        }

        if (status === StatusBadgeOptions.OUTBOUND_INACTIVE) {
            return `Outbound ${getPageDisplayName(pageName)} configuration is Inactive. Click to Activate from the menu.`;
        }

        return `${getPageDisplayName(pageName)} configuration is Inactive. Click to Activate from the menu.`;
    };

    const getStatusLabel = (): string => {
        if (pageName === BulkProtectionOptions.ALLOW_LIST) {
            return `ALLOWED IPs ${status.toUpperCase()}`;
        }
        if (pageName === BulkProtectionOptions.BLOCK_LIST) {
            return `BLOCKED IPs ${status.toUpperCase()}`;
        }
        return status.toUpperCase();
    };

    if (status) {
        return <span className={`status-badge ${status}`}>
            {getStatusLabel()}
            {status.includes(StatusBadgeOptions.INACTIVE) &&
                <Popover placement={'bottom'} content={getTooltipMessage()} color={'#1D2329'}
                         overlayClassName={'activate-tooltip'}>
                    <InfoButton/>
                </Popover>
            }
            </span>;
    } else {
        return <></>;
    }
});